import "@/App.css";
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { SpotlightProvider } from "./contexts/SpotlightContext";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { AuthCallback } from "./components/AuthCallback";
import { Toaster } from "./components/ui/sonner";
import { OrgPicker } from "./components/OrgPicker";
import { CelebrationConfetti } from "./components/WelcomeConfetti";
import axios from "axios";

// Pages
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import RegisterCompany from "./pages/RegisterCompany";
import PaymentSuccess from "./pages/PaymentSuccess";
import InvitationSignup from "./pages/InvitationSignup";
import BrowseAgencies from "./pages/BrowseAgencies";
import JobListings from "./pages/JobListings";
import VideoCall from "./pages/VideoCall";
import SubscriptionDashboard from "./pages/SubscriptionDashboard";
import Timesheets from "./pages/Timesheets";
import SupportPage from "./pages/SupportPage";
import PrivacyPage from "./pages/PrivacyPage";
import CopyrightPage from "./pages/CopyrightPage";
import WellnessGoals from "./pages/WellnessGoals";
import AnalyticsDashboard from "./pages/AnalyticsDashboard";
import AvailabilityCalendar from "./pages/AvailabilityCalendar";
import ShiftSwaps from "./pages/ShiftSwaps";
import DocumentTemplates from "./pages/DocumentTemplates";
import LeaveRequests from "./pages/LeaveRequests";
import CaregiverProfile from "./pages/CaregiverProfile";
import TeamChat from "./pages/TeamChat";
import Medications from "./pages/Medications";
import ClinicalLogs from "./pages/ClinicalLogs";
import IncidentReports from "./pages/IncidentReports";
import Broadcasts from "./pages/Broadcasts";
import CareJournals from "./pages/CareJournals";
import DocumentStorage from "./pages/DocumentStorage";
import AIScheduling from "./pages/AIScheduling";
import Terms from "./pages/Terms";
import DeleteData from "./pages/DeleteData";
import UserSettings from "./pages/UserSettings";
import Messaging from "./pages/Messaging";
import PerformanceReviews from "./pages/PerformanceReviews";
import LearningRewards from "./pages/LearningRewards";
import PayrollProcessing from "./pages/PayrollProcessing";
import NetworkSwitcher from "./pages/NetworkSwitcher";
import ManageJobPostings from "./pages/ManageJobPostings";
import ShiftManagement from "./pages/ShiftManagement";
import CallbackRequests from "./pages/CallbackRequests";
import SignUp from "./pages/SignUp";
import AdminCaregivers from "./pages/AdminCaregivers";
import AdminClients from "./pages/AdminClients";
import AdminNotifications from "./pages/AdminNotifications";
import BulkInvite from "./pages/BulkInvite";
import AuditLogs from "./pages/AuditLogs";
import AdminInvoices from "./pages/AdminInvoices";
import StaffBankDetails from "./pages/StaffBankDetails";
import StaffDiscipline from "./pages/StaffDiscipline";
import SecuritySettings from "./pages/SecuritySettings";
import PermissionsManagement from "./pages/PermissionsManagement";
import AgencyVerification from "./pages/AgencyVerification";
import OnboardingWizard from "./pages/OnboardingWizard";
import InterviewScheduling from "./pages/InterviewScheduling";
import RewardsAdmin from "./pages/RewardsAdmin";
import ClientFolders from "./pages/ClientFolders";
import CaregiverCompliance from "./pages/CaregiverCompliance";
import LiveTracking from "./pages/LiveTracking";
import MyCompliance from "./pages/MyCompliance";
import CaregiverTasks from "./pages/CaregiverTasks";
import CaregiverRewards from "./pages/CaregiverRewards";
import CaregiverCertifications from "./pages/CaregiverCertifications";
import CaregiverNotifications from "./pages/CaregiverNotifications";
import OfflineEmergencyInfo from "./pages/OfflineEmergencyInfo";
import ClientProfile from "./pages/ClientProfile";
import ClientPhotos from "./pages/ClientPhotos";
import ClientRateAgency from "./pages/ClientRateAgency";
import ClientRateCaregiver from "./pages/ClientRateCaregiver";
import ClientDocuments from "./pages/ClientDocuments";
import ClientCareTracking from "./pages/ClientCareTracking";
import MarketingAssets from "./pages/MarketingAssets";
import TutorialGuide from "./pages/TutorialGuide";
import CarePlanTemplates from "./pages/CarePlanTemplates";
import ClinicalCompliance from "./pages/ClinicalCompliance";
import GovernancePage from "./pages/GovernancePage";
import HealthQuality from "./pages/HealthQuality";
import IGCompliance from "./pages/IGCompliance";
import NHSConnect from "./pages/NHSConnect";
import InspectorDashboard from "./pages/InspectorDashboard";
import ClientRecord from "./pages/ClientRecord";
import DSPTDashboard from "./pages/DSPTDashboard";
import CQCReadiness from "./pages/CQCReadiness";
import DSPTAssessment from "./pages/DSPTAssessment";
import EmarPage from "./pages/EmarPage";
import MedicationRound from "./pages/MedicationRound";
import VisitAuditPage from "./pages/VisitAuditPage";
import VisitHeatmap from "./pages/VisitHeatmap";
import ComplianceCertificate from "./pages/ComplianceCertificate";
import TrainingMatrix from "./pages/TrainingMatrix";
import HIQAAssessment from "./pages/HIQAAssessment";
import CaregiverScorecard from "./pages/CaregiverScorecard";
import HRComplianceDashboard from "./pages/HRComplianceDashboard";
import ComplianceScoreDashboard from "./pages/ComplianceScoreDashboard";
import StaffOnboarding from "./pages/StaffOnboarding";
import ComplianceChecklists from "./pages/ComplianceChecklists";

// Dashboards
import AdminDashboard from "./pages/dashboards/AdminDashboard";
import CEODashboard from "./pages/dashboards/CEODashboard";
import CaregiverDashboard from "./pages/dashboards/CaregiverDashboard";
import ClientDashboard from "./pages/dashboards/ClientDashboard";
import FamilyDashboard from "./pages/dashboards/FamilyDashboard";
import DeveloperDashboard from "./pages/dashboards/DeveloperDashboard";

// Dashboard Redirect Component with Multi-Org Picker & Birthday Celebrations
function DashboardRedirect() {
  const { user, loading } = useAuth();
  const [showOrgPicker, setShowOrgPicker] = React.useState(true);
  const [orgPickerDone, setOrgPickerDone] = React.useState(false);
  const [celebration, setCelebration] = React.useState(null);

  React.useEffect(() => {
    if (user && orgPickerDone) {
      // Check for birthday / milestones
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      axios.get(`${API_URL}/api/org/birthday-check`, { withCredentials: true })
        .then(({ data }) => {
          if (data.has_celebrations && data.celebrations.length > 0) {
            setCelebration(data.celebrations[0]);
          }
        }).catch(() => {});
    }
  }, [user, orgPickerDone]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Show org picker if user has multiple orgs
  if (showOrgPicker && !orgPickerDone) {
    return (
      <OrgPicker
        onSelect={() => { setOrgPickerDone(true); setShowOrgPicker(false); }}
        onSkip={() => { setOrgPickerDone(true); setShowOrgPicker(false); }}
      />
    );
  }

  const dashboardRoutes = {
    agency_admin: '/dashboard/admin',
    ceo: '/dashboard/ceo',
    caregiver: '/dashboard/caregiver',
    client: '/dashboard/client',
    family: '/dashboard/family',
    developer: '/dashboard/developer',
    potential_client: '/browse',
    job_seeker: '/jobs'
  };

  const redirectPath = dashboardRoutes[user.role] || '/';

  return (
    <>
      {celebration && (
        <CelebrationConfetti
          show={true}
          type={celebration.type}
          onComplete={() => setCelebration(null)}
        />
      )}
      {celebration && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={() => setCelebration(null)} data-testid="celebration-overlay">
          <div className="glass-card rounded-2xl p-8 text-center max-w-sm mx-4">
            <p className="text-4xl mb-4">{celebration.type === 'birthday' ? '🎂' : '🎉'}</p>
            <h2 className="text-xl font-heading font-bold text-foreground">{celebration.message}</h2>
            <p className="text-sm text-muted-foreground mt-2">Click anywhere to continue</p>
          </div>
        </div>
      )}
      <Navigate to={redirectPath} replace />
    </>
  );
}

// App Router that handles OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id during render (NOT in useEffect)
  // This prevents race conditions by processing OAuth callback FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/register-company" element={<RegisterCompany />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/invite/:token" element={<InvitationSignup />} />
      <Route path="/invite" element={<InvitationSignup />} />
      <Route path="/browse" element={<BrowseAgencies />} />
      <Route path="/jobs" element={<JobListings />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/support" element={<SupportPage />} />
      <Route path="/copyright" element={<CopyrightPage />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/delete-data" element={<DeleteData />} />
      <Route path="/inspect/:token" element={<InspectorDashboard />} />
      <Route
        path="/client-record/:clientId"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <ClientRecord />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dspt-dashboard"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <DSPTDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/cqc-readiness"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <CQCReadiness />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dspt-assessment"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <DSPTAssessment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emar/:clientId"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <EmarPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/medication-round"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <MedicationRound />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visit-audit"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <VisitAuditPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/visit-heatmap"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <VisitHeatmap />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance-certificate"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <ComplianceCertificate />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training-matrix"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <TrainingMatrix />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hiqa-assessment"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <HIQAAssessment />
          </ProtectedRoute>
        }
      />
      <Route
        path="/caregiver-scorecard"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <CaregiverScorecard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/hr-compliance"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <HRComplianceDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance-scores"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <ComplianceScoreDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance-checklists"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <ComplianceChecklists />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-onboarding/:staffId"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <StaffOnboarding />
          </ProtectedRoute>
        }
      />




      {/* Dashboard Redirect */}
      <Route path="/dashboard" element={<DashboardRedirect />} />

      {/* Protected Dashboard Routes */}
      <Route
        path="/video"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <VideoCall />
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <SubscriptionDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/caregivers"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AdminCaregivers />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/clients"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/notifications"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AdminNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/bulk-invite"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <BulkInvite />
          </ProtectedRoute>
        }
      />
      <Route
        path="/audit-logs"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AuditLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AdminInvoices />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-bank-details"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <StaffBankDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="/staff-discipline"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <StaffDiscipline />
          </ProtectedRoute>
        }
      />
      <Route
        path="/security-settings"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family', 'developer']}>
            <SecuritySettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/permissions"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <PermissionsManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/verification"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AgencyVerification />
          </ProtectedRoute>
        }
      />
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <OnboardingWizard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviews"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <InterviewScheduling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/rewards-admin"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <RewardsAdmin />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client-folders"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <ClientFolders />
          </ProtectedRoute>
        }
      />
      <Route
        path="/caregiver-compliance"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <CaregiverCompliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/live-tracking"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <LiveTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-compliance"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <MyCompliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-tasks"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <CaregiverTasks />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-rewards"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <CaregiverRewards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-certifications"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <CaregiverCertifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-notifications"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <CaregiverNotifications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/emergency-info"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <OfflineEmergencyInfo />
          </ProtectedRoute>
        }
      />
      <Route
        path="/timesheets"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <Timesheets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/wellness-goals"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <WellnessGoals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/analytics"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AnalyticsDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/availability"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <AvailabilityCalendar />
          </ProtectedRoute>
        }
      />
      <Route
        path="/shift-swaps"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <ShiftSwaps />
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-templates"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <DocumentTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/leave-requests"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <LeaveRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/my-profile"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <CaregiverProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team-chat"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <TeamChat />
          </ProtectedRoute>
        }
      />
      <Route
        path="/medications"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <Medications />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clinical-logs"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <ClinicalLogs />
          </ProtectedRoute>
        }
      />
      <Route
        path="/incident-reports"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <IncidentReports />
          </ProtectedRoute>
        }
      />
      <Route
        path="/broadcasts"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <Broadcasts />
          </ProtectedRoute>
        }
      />
      <Route
        path="/care-journals"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <CareJournals />
          </ProtectedRoute>
        }
      />
      <Route
        path="/document-storage"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <DocumentStorage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ai-scheduling"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AIScheduling />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family', 'developer']}>
            <UserSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messaging"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family']}>
            <Messaging />
          </ProtectedRoute>
        }
      />
      <Route
        path="/performance-reviews"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <PerformanceReviews />
          </ProtectedRoute>
        }
      />
      <Route
        path="/learning"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <LearningRewards />
          </ProtectedRoute>
        }
      />
      <Route
        path="/payroll"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <PayrollProcessing />
          </ProtectedRoute>
        }
      />
      <Route
        path="/networks"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <NetworkSwitcher />
          </ProtectedRoute>
        }
      />
      <Route
        path="/manage-jobs"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <ManageJobPostings />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/shifts"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <ShiftManagement />
          </ProtectedRoute>
        }
      />
      <Route
        path="/callback-requests"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <CallbackRequests />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/admin/*"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketing-assets"
        element={
          <ProtectedRoute allowedRoles={['developer']}>
            <MarketingAssets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/guide"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client', 'family', 'developer']}>
            <TutorialGuide />
          </ProtectedRoute>
        }
      />
      <Route
        path="/care-plan-templates"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <CarePlanTemplates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/clinical-compliance"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver', 'client']}>
            <ClinicalCompliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/governance"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <GovernancePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/health-quality"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <HealthQuality />
          </ProtectedRoute>
        }
      />
      <Route
        path="/ig-compliance"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo']}>
            <IGCompliance />
          </ProtectedRoute>
        }
      />
      <Route
        path="/nhs-connect"
        element={
          <ProtectedRoute allowedRoles={['agency_admin', 'ceo', 'caregiver']}>
            <NHSConnect />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/ceo/*"
        element={
          <ProtectedRoute allowedRoles={['ceo']}>
            <CEODashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/caregiver/*"
        element={
          <ProtectedRoute allowedRoles={['caregiver']}>
            <CaregiverDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/profile"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/photos"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientPhotos />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/rate-agency"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientRateAgency />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/rate-caregiver"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientRateCaregiver />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/documents"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDocuments />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/caregivers"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientRateCaregiver />
          </ProtectedRoute>
        }
      />
      <Route
        path="/client/care-tracking"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientCareTracking />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/client/*"
        element={
          <ProtectedRoute allowedRoles={['client']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/family/*"
        element={
          <ProtectedRoute allowedRoles={['family']}>
            <FamilyDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard/developer/*"
        element={
          <ProtectedRoute allowedRoles={['developer']}>
            <DeveloperDashboard />
          </ProtectedRoute>
        }
      />

      {/* Catch all - redirect to home */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <LanguageProvider>
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <SpotlightProvider>
            <AppRouter />
          </SpotlightProvider>
        </BrowserRouter>
        <Toaster position="top-right" />
      </AuthProvider>
    </ThemeProvider>
    </LanguageProvider>
  );
}

export default App;
